import { z } from "zod";

export const QaRealisticDemoRosterRecipeIdSchema = z.literal(
  "realistic_demo_roster_v1",
);

const QaRealisticDemoRosterStudentSchema = z.object({
  studentId: z.string().uuid().optional(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  grade: z.string().min(1),
  division: z.string().min(1),
  tuitionCents: z.number().int().min(800_000).max(1_600_000),
});

const QaRealisticDemoRosterAppliedStudentSchema =
  QaRealisticDemoRosterStudentSchema.extend({
    studentId: z.string().uuid(),
    childAppId: z.string().uuid(),
    gender: z.enum(["male", "female"]),
  });

const QaRealisticDemoRosterFamilySchema = z.object({
  key: z.enum(["miri", "rasmus", "david", "sam", "avi", "agent_qa"]),
  familyId: z.string().uuid().optional(),
  familyName: z.string().min(1),
  guardianEmail: z.string().email(),
  billedCents: z.number().int().nonnegative(),
  paidCents: z.number().int().nonnegative(),
  outstandingCents: z.number().int().nonnegative(),
  students: z.array(QaRealisticDemoRosterStudentSchema).min(1),
});

const QaRealisticDemoRosterAppliedFamilySchema =
  QaRealisticDemoRosterFamilySchema.omit({ students: true }).extend({
    familyId: z.string().uuid(),
    applicantId: z.string().uuid(),
    students: z.array(QaRealisticDemoRosterAppliedStudentSchema).min(1),
  });

const QaRealisticDemoRosterSnapshotSchema = z.object({
  familyCount: z.number().int().nonnegative(),
  studentCount: z.number().int().nonnegative(),
  billedCents: z.number().int().nonnegative(),
  paidCents: z.number().int().nonnegative(),
  outstandingCents: z.number().int().nonnegative(),
});

const QaRealisticDemoRosterLifecycleCountSchema = z
  .number()
  .int()
  .nonnegative()
  .nullable();

const QaRealisticDemoRosterLifecycleDigestSchema = z
  .string()
  .regex(/^[a-f0-9]{64}$/i)
  .nullable();

export const QaRealisticDemoRosterLifecycleSchema = z.object({
  state: z.enum(["none", "applied", "rolled_back", "corrupt", "ambiguous"]),
  activeReleaseEvidenceId: z.string().min(1).nullable(),
  activeExternallyVerifiedDeployedSha: z
    .string()
    .regex(/^[a-f0-9]{40}$/i)
    .nullable(),
  receiptReference: z.string().min(1).nullable(),
  appliedAt: z.string().min(1).nullable(),
  rolledBackAt: z.string().min(1).nullable(),
  semanticRosterDigest: QaRealisticDemoRosterLifecycleDigestSchema,
  afterStateDigest: QaRealisticDemoRosterLifecycleDigestSchema,
  familyCount: QaRealisticDemoRosterLifecycleCountSchema,
  studentCount: QaRealisticDemoRosterLifecycleCountSchema,
  ledgerEntryCount: QaRealisticDemoRosterLifecycleCountSchema,
  contactCount: QaRealisticDemoRosterLifecycleCountSchema,
  restoredFamilyCount: QaRealisticDemoRosterLifecycleCountSchema,
  restoredStudentCount: QaRealisticDemoRosterLifecycleCountSchema,
  reversedLedgerEntryCount: QaRealisticDemoRosterLifecycleCountSchema,
  financialAidRecoveryStatus: z.enum([
    "none",
    "pending",
    "completed",
    "unknown",
  ]),
  rollbackSagaStatus: z.enum([
    "none",
    "pending",
    "compensated",
    "completed",
    "unknown",
  ]),
  matchesRequestedRelease: z.boolean(),
  matchesKnownLegacy: z.boolean(),
});

export const QaRealisticDemoRosterPreviewInputSchema = z.object({
  schoolId: z.string().min(1),
  tenantSlug: z.string().min(1),
  releaseEvidenceId: z.string().min(1).max(200),
  externallyVerifiedDeployedSha: z.string().regex(/^[a-f0-9]{40}$/i),
  cleanupOwner: z.string().min(3).max(200),
});

export const QaRealisticDemoRosterPreviewSchema = z.object({
  recipeId: QaRealisticDemoRosterRecipeIdSchema,
  environment: z.literal("dev"),
  orgId: z.string().uuid(),
  schoolId: z.string().min(1),
  tenantSlug: z.string().min(1),
  academicYear: z.string().min(1),
  releaseEvidenceId: z.string().min(1),
  externallyVerifiedDeployedSha: z.string().regex(/^[a-f0-9]{40}$/i),
  cleanupOwner: z.string().min(3),
  lifecycle: QaRealisticDemoRosterLifecycleSchema,
  current: QaRealisticDemoRosterSnapshotSchema,
  target: QaRealisticDemoRosterSnapshotSchema,
  families: z.array(QaRealisticDemoRosterFamilySchema).length(6),
  archiveFamilyCount: z.number().int().nonnegative(),
  canApply: z.boolean(),
  beforeStateDigest: z.string().min(1),
  confirmationText: z.string().min(1),
  releaseBinding: z.literal("externally_verified_release_preflight"),
});

export const QaRealisticDemoRosterApplyInputSchema =
  QaRealisticDemoRosterPreviewInputSchema.extend({
    expectedBeforeStateDigest: z.string().min(1),
    confirmationText: z.string().min(1),
  });

export const QaRealisticDemoRosterApplyResultSchema = z.object({
  status: z.enum(["applied", "already_applied"]),
  preview: QaRealisticDemoRosterPreviewSchema,
  families: z.array(QaRealisticDemoRosterAppliedFamilySchema).length(6),
  receiptReference: z.string().min(1),
  rollbackToken: z.string().min(1),
  rollbackConfirmationText: z.string().min(1),
  afterStateDigest: z.string().min(1),
  appliedAt: z.string().min(1),
  releaseBinding: z.literal("externally_verified_release_preflight"),
});

export const QaRealisticDemoRosterRollbackInputSchema = z.object({
  schoolId: z.string().min(1),
  tenantSlug: z.string().min(1),
  releaseEvidenceId: z.string().min(1).max(200),
  externallyVerifiedDeployedSha: z.string().regex(/^[a-f0-9]{40}$/i),
  rollbackToken: z.string().min(1),
  expectedStateDigest: z.string().min(1),
  confirmationText: z.string().min(1),
});

export const QaRealisticDemoRosterRollbackResultSchema = z.object({
  status: z.enum(["rolled_back", "already_rolled_back"]),
  receiptReference: z.string().min(1),
  reversedLedgerEntryCount: z.number().int().nonnegative(),
  restoredFamilyCount: z.number().int().nonnegative(),
  restoredStudentCount: z.number().int().nonnegative(),
  afterStateDigest: z.string().min(1),
  rolledBackAt: z.string().min(1),
  releaseBinding: z.literal("externally_verified_release_preflight"),
});

export type QaRealisticDemoRosterPreview = z.infer<
  typeof QaRealisticDemoRosterPreviewSchema
>;
export type QaRealisticDemoRosterApplyResult = z.infer<
  typeof QaRealisticDemoRosterApplyResultSchema
>;
export type QaRealisticDemoRosterRollbackResult = z.infer<
  typeof QaRealisticDemoRosterRollbackResultSchema
>;
